import type {TCalibrationResponse} from "api/polarisApi/types";
import type {TCalibrationSampling} from "./types";
import {errorCodeOf, TPolarisApiError} from "./thunkUtils";

type TCalibration = TCalibrationResponse | null | undefined;

// Code on the error the resample thunk throws when the fresh sample has not
// arrived by the end of its polling window.
export const CALIBRATION_SAMPLE_TIMEOUT = "calibration_sample_timeout";

export const calibrationSampleTimeoutError = (): TPolarisApiError =>
	Object.assign(new Error("We couldn't pull a calibration sample."), {
		code: CALIBRATION_SAMPLE_TIMEOUT,
	});

export const isCalibrationSampleTimeout = (error: unknown): boolean =>
	errorCodeOf(error) === CALIBRATION_SAMPLE_TIMEOUT;

// The round on screen. The backend names it round_id; older payloads (and
// fixtures) carry sample_id.
export const calibrationRoundOf = (calibration: TCalibration): string | null =>
	calibration?.round_id ?? calibration?.sample_id ?? null;

// What the calibration card shows:
//   loading  a sample is on its way (first build, resample or Try again)
//   failed   it did not arrive: offer Try again
//   ready    there are people to vote on
// Without a `generation` field (older backend) zero candidates reads as loading.
export type TCalibrationSampleView = "loading" | "failed" | "ready";

export const calibrationSampleView = (
	calibration: TCalibration,
	sampling: TCalibrationSampling,
): TCalibrationSampleView => {
	if (sampling === "loading") return "loading";
	if ((calibration?.candidates.length ?? 0) > 0) return "ready";
	if (sampling === "failed" || calibration?.generation?.status === "failed") return "failed";

	return "loading";
};

// Whether the fallback poll should keep re-reading the calibration: no
// candidates yet and the backend has not given up (a failed build waits for
// Try again).
export const isCalibrationAwaitingSample = (calibration: TCalibration): boolean =>
	(calibration?.candidates.length ?? 0) === 0 && calibration?.generation?.status !== "failed";
